package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.DashboardTheme
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private object FahrerLanguageDropdownStyles : StyleSheet() {
    val rootClass by style {
        position(Position.Relative)
        width(100.percent)
    }

    val trigger by style {
        width(100.percent)
        minHeight(76.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(10.px, 12.px)
        boxSizing("border-box")
        backgroundColor(Color.transparent)
        color(DashboardTheme.TEXT_LIGHT)
        border(0.px)
        borderRadius(10.px)
        cursor("pointer")
        position(Position.Relative)
        property("backdrop-filter", "blur(15px)")
        property("transition", "box-shadow 0.16s ease")
        hover {
            property("box-shadow", "0 0 18px rgba(255,255,255,0.08)")
        }
    }

    val selected by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(6.px)
        minWidth(0.px)
    }

    val flag by style {
        width(34.px)
        height(34.px)
        property("object-fit", "contain")
        flexShrink(0)
    }

    val label by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(15.px)
        fontWeight("600")
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
    }

    val arrow by style {
        width(16.px)
        height(16.px)
        opacity(0.72)
        flexShrink(0)
        property("filter", "brightness(0) invert(1)")
        position(Position.Absolute)
        right(8.px)
        top(50.percent)
        property("transform", "translateY(-50%)")
    }

    val menu by style {
        position(Position.Fixed)
        left(50.percent)
        top(50.percent)
        property("transform", "translate(-50%, -50%)")
        property("z-index", "1000")
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        borderRadius(10.px)
        property("box-shadow", "0 18px 40px ${DashboardTheme.SURFACE_SHADOW}")
        property("backdrop-filter", "blur(18px)")
        width(156.px)
        maxHeight(360.px)
        overflowY("auto")
        overflowX("hidden")
    }

    val option by style {
        minHeight(72.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(6.px)
        padding(8.px, 12.px)
        boxSizing("border-box")
        color(DashboardTheme.TEXT_LIGHT)
        cursor("pointer")
        hover {
            backgroundColor(Color("rgba(255,255,255,0.08)"))
        }
    }

    val optionActive by style {
        property("background", "rgba(255,0,0,0.10)")
    }
}

@Composable
fun FahrerLanguageDropdown(
    selectedLanguage: String,
    onLanguage: (String) -> Unit,
    compact: Boolean = false
) {
    Style(FahrerLanguageDropdownStyles)

    var expanded by remember { mutableStateOf(false) }
    val selected = LocalizationManager.languages.firstOrNull { it.code == selectedLanguage }
        ?: LocalizationManager.languages.first()

    Div({ classes(FahrerLanguageDropdownStyles.rootClass) }) {
        Div({
            classes(FahrerLanguageDropdownStyles.trigger)
            if (compact) {
                style {
                    minHeight(0.px)
                    padding(0.px)
                    property("backdrop-filter", "none")
                    borderRadius(0.px)
                    property("box-shadow", "none")
                }
            }
            attr("role", "button")
            attr("aria-haspopup", "listbox")
            attr("aria-expanded", expanded.toString())
            tabIndex(0)
            onClick { expanded = !expanded }
            onKeyDown {
                if (it.key == "Enter" || it.key == " ") {
                    it.preventDefault()
                    expanded = !expanded
                }
                if (it.key == "Escape") expanded = false
            }
        }) {
            Div({
                classes(FahrerLanguageDropdownStyles.selected)
                if (compact) style {
                    flexDirection(FlexDirection.Row)
                    gap(8.px)
                }
            }) {
                Img(src = LocalizationManager.flagPathFor(selected.code), attrs = {
                    classes(FahrerLanguageDropdownStyles.flag)
                    if (compact) style {
                        width(18.px)
                        height(18.px)
                    }
                })
                Span({ classes(FahrerLanguageDropdownStyles.label) }) { Text(selected.name) }
            }
        }

        if (expanded) {
            Div({
                classes(FahrerLanguageDropdownStyles.menu)
                attr("role", "listbox")
            }) {
                LocalizationManager.languages.forEach { language ->
                    Div({
                        classes(FahrerLanguageDropdownStyles.option)
                        if (language.code == selectedLanguage) classes(FahrerLanguageDropdownStyles.optionActive)
                        attr("role", "option")
                        attr("aria-selected", (language.code == selectedLanguage).toString())
                        onClick {
                            onLanguage(language.code)
                            expanded = false
                        }
                    }) {
                        Img(src = LocalizationManager.flagPathFor(language.code), attrs = {
                            classes(FahrerLanguageDropdownStyles.flag)
                        })
                        Span({ classes(FahrerLanguageDropdownStyles.label) }) { Text(language.name) }
                    }
                }
            }
        }
    }
}
